import React, { useMemo, useRef } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridCustomCellProps,
  GridNoRecords,
  GridPageChangeEvent,
  GridSortChangeEvent
} from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { Button } from '@progress/kendo-react-buttons';
import { hyperlinkOpenIcon } from '@progress/kendo-svg-icons';
import { SortDescriptor } from '@progress/kendo-data-query';
import {
  CONTENT_TYPE_FIELD,
  CPS_FIELD_TYPE,
  CpsFieldForView,
  CpsFieldType,
  DOC_ID_FIELD,
  EMPTY_VALUE,
  EXTENSION_FIELD,
  GRID_PAGE_SIZES
} from '@cs-cms/types';
import CpsLoader from '@cs-cms/ui/common/CpsLoader';
import { BooleanCell, CpsGridCell, ExtensionCell } from '@cs-cms/ui';
import { useCpsGridLayout } from '../../../../../packages/ui/src/grid/hooks/useCpsGridLayout';
import DateCell from '../../../../../packages/ui/src/grid/customCells/DateCell';
import DateTimeCell from '../../../../../packages/ui/src/grid/customCells/DateTimeCell';
import { SapInvoiceGridDocument } from '../lib/useSapInvoiceDocuments';
import './SapInvoiceDocumentsGrid.scss';

interface SapInvoiceDocumentsGridProps {
  columns: CpsFieldForView[] | undefined;
  gridData: SapInvoiceGridDocument[];
  totalCount: number;
  skip: number;
  pageSize: number;
  sort: SortDescriptor[] | undefined;
  isLoading: boolean;
  openDocumentLoading: boolean;
  translate: (key: string) => string;
  onPageChange: (event: GridPageChangeEvent) => void;
  onSortChange: (descriptors: SortDescriptor[]) => void;
  openDocument: (contentType: string, documentId: string) => void;
  indexId: number | undefined;
}

const mapBaseCellProps = (cellProps: GridCellProps): GridCustomCellProps => ({
  dataItem: cellProps.dataItem,
  field: cellProps.field,
  dataIndex: cellProps.dataIndex,
  id: cellProps.id,
  columnPosition: cellProps.columnPosition,
  onChange: cellProps.onChange,
  className: cellProps.className,
  colSpan: cellProps.colSpan,
  ariaColumnIndex: cellProps.ariaColumnIndex,
  isSelected: cellProps.isSelected,
  style: cellProps.style,
  rowType: cellProps.rowType,
  level: cellProps.level,
  expanded: cellProps.expanded,
  tdProps: (cellProps as GridCustomCellProps).tdProps
});

const getCellType = (fieldName: string, columns: CpsFieldForView[] | undefined): CpsFieldType => {
  const column = columns?.find(field => field.name === fieldName);
  if (!column) {
    return CPS_FIELD_TYPE.TEXT;
  }
  return column.type;
};

const isLinkedDocumentField = (fieldName: string, columns: CpsFieldForView[] | undefined): boolean => {
  return Boolean(columns?.find(field => field.name === fieldName && field.isDocumentLink));
};

interface DocumentLinkCellProps extends GridCustomCellProps {
  onLinkClicked: () => void;
  title?: string;
  value?: string;
}

const DocumentLinkCell: React.FC<DocumentLinkCellProps> = props => {
  const field = props.field || EMPTY_VALUE;
  const value = props.value || props.dataItem[field];
  const title = props.title || value?.toString() || EMPTY_VALUE;

  return (
    <CpsGridCell
      dataItem={props.dataItem}
      field={props.field}
      dataIndex={props.dataIndex}
      id={props.id}
      tdProps={props.tdProps}
      columnPosition={props.columnPosition}
      onChange={props.onChange}
      className={props.className}
      colSpan={props.colSpan}
      ariaColumnIndex={props.ariaColumnIndex}
      isSelected={props.isSelected}
      style={props.style}
      rowType={props.rowType}
      level={props.level}
      expanded={props.expanded}
      title={title}
    >
      <div className="linkedCell">
        <Button svgIcon={hyperlinkOpenIcon} title={title} size="small" fillMode="link" onClick={props.onLinkClicked}>
          {value?.toString() || EMPTY_VALUE}
        </Button>
      </div>
    </CpsGridCell>
  );
};

export const SapInvoiceDocumentsGrid: React.FC<SapInvoiceDocumentsGridProps> = ({
  columns,
  gridData,
  totalCount,
  skip,
  pageSize,
  sort,
  isLoading,
  openDocumentLoading,
  translate,
  onPageChange,
  onSortChange,
  openDocument,
  indexId
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const { gridDimensions } = useCpsGridLayout({
    gridRef,
    gridDataLength: gridData.length,
    selectedIndexId: indexId
  });

  const cellRender = useMemo(() => {
    return (cellProps: GridCellProps): React.ReactNode => {
      const field = cellProps.field;
      if (!field) {
        return null;
      }

      const baseProps = mapBaseCellProps(cellProps);
      const rawValue = cellProps.dataItem[field];

      if (field === EXTENSION_FIELD) {
        return <ExtensionCell {...baseProps} />;
      }

      if (isLinkedDocumentField(field, columns)) {
        const stringValue = rawValue?.toString();
        return (
          <DocumentLinkCell
            {...baseProps}
            value={stringValue}
            title={stringValue}
            onLinkClicked={() => {
              const docId = cellProps.dataItem[DOC_ID_FIELD] as string;
              const contentType = cellProps.dataItem[CONTENT_TYPE_FIELD] as string;
              openDocument(contentType, docId);
            }}
          />
        );
      }

      const cellType = getCellType(field, columns);

      if (cellType === CPS_FIELD_TYPE.DATE) {
        return <DateCell {...baseProps} />;
      }
      if (cellType === CPS_FIELD_TYPE.DATETIME) {
        return <DateTimeCell {...baseProps} />;
      }
      if (cellType === CPS_FIELD_TYPE.BOOLEAN) {
        return <BooleanCell {...baseProps} />;
      }

      let displayValue = EMPTY_VALUE;
      if (Array.isArray(rawValue)) {
        displayValue = rawValue.join(', ');
      } else if (rawValue != null) {
        displayValue = rawValue.toString();
      }

      return (
        <CpsGridCell {...baseProps} title={displayValue}>
          {displayValue}
        </CpsGridCell>
      );
    };
  }, [columns, openDocument]);

  return (
    <div ref={gridRef} className="cpsGridContainer">
      {(isLoading || openDocumentLoading) && (
        <CpsLoader
          type={openDocumentLoading ? 'pulsing' : 'infinite-spinner'}
          textMessageKey={openDocumentLoading ? 'custom.common.loader.opening' : undefined}
        />
      )}
      <Tooltip openDelay={500} anchorElement="target" parentTitle={true} position="bottom">
        <Grid
          className="cpsGrid sapInvoiceGrid"
          style={{ height: gridDimensions.height }}
          sortable={true}
          sort={sort}
          onSortChange={(event: GridSortChangeEvent) => onSortChange(event.sort)}
          pageable={{
            buttonCount: 4,
            pageSizes: [...GRID_PAGE_SIZES],
            info: true
          }}
          scrollable="scrollable"
          skip={skip}
          take={pageSize}
          total={totalCount}
          onPageChange={onPageChange}
          data={gridData}
          dataItemKey={DOC_ID_FIELD}
          navigatable={true}
          resizable={true}
          size="small"
          cells={{ data: cellRender }}
        >
          <GridNoRecords>
            <div className="noRecordsRow">{translate('custom.common.emptyGridMessage')}</div>
          </GridNoRecords>
          {columns?.map(column => (
            <GridColumn
              key={column.id}
              field={column.name}
              title={column.displayName}
              sortable={column.sortable}
              width={column.width}
            />
          ))}
        </Grid>
      </Tooltip>
    </div>
  );
};
